import type { JSX } from 'react';
import { Alert, Chip, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client.js';
import { AsyncSection } from '../components/AsyncSection.js';
import { DataTable } from '../components/DataTable.js';

interface UserRow {
  id: string;
  name: string;
  email: string;
  emailVerified: boolean;
  status: string;
  mfaEnabled: boolean;
  createdAt: string;
  platformRoles: string[];
  activeCircles: number;
  circleRoles: string[];
  activeSessions: number;
}

export function UsersPage(): JSX.Element {
  const query = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () => api.get<{ items: UserRow[] }>('/v1/admin/users?limit=50'),
  });

  return (
    <>
      <Typography variant="h1" gutterBottom>
        Contas
      </Typography>
      <Alert severity="info" sx={{ mb: 3 }}>
        Somente metadados: nome, e-mail, papéis, sessões e contagens. O painel não lista
        conteúdo privado de família — nem por padrão, nem para administradores.
      </Alert>
      <AsyncSection query={query} emptyMessage="Nenhuma conta cadastrada.">
        {(data) => (
          <DataTable
            rows={data.items}
            getRowKey={(row) => row.id}
            emptyMessage="Nenhuma conta cadastrada."
            columns={[
              { key: 'name', label: 'Nome', render: (row) => row.name },
              { key: 'email', label: 'E-mail', render: (row) => row.email },
              {
                key: 'state',
                label: 'Estado',
                render: (row) => (
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    <Chip size="small" label={row.status} />
                    <Chip
                      size="small"
                      variant="outlined"
                      color={row.emailVerified ? 'success' : 'warning'}
                      label={row.emailVerified ? 'e-mail verificado' : 'e-mail não verificado'}
                    />
                    {row.mfaEnabled ? <Chip size="small" color="success" variant="outlined" label="MFA" /> : null}
                  </Stack>
                ),
              },
              {
                key: 'circles',
                label: 'Círculos',
                render: (row) =>
                  row.activeCircles === 0
                    ? '—'
                    : `${row.activeCircles} (${row.circleRoles.join(', ')})`,
              },
              {
                key: 'roles',
                label: 'Papéis de plataforma',
                render: (row) => (row.platformRoles.length > 0 ? row.platformRoles.join(', ') : '—'),
              },
              { key: 'sessions', label: 'Sessões', render: (row) => String(row.activeSessions), align: 'right' },
            ]}
          />
        )}
      </AsyncSection>
    </>
  );
}